import React, {memo} from 'react';

import ExternalLinkIcon from '../images/external-link.svg';

import {pageMetadataForPath} from '../utils/page';

import LocaleLink from './locale-link';

import style from './link.module.scss';

const URL_PAGES_REGEX = /^\/lichtparcours2020\/([a-z-]+)\/([A-Za-z0-9_-]+)\.json$/;

export default memo(
	/**
	 * @param {{
	 *     addExternalIcon?: boolean,
	 *     externalIconClassName?: string,
	 *     externalClassName?: string,
	 *     internalClassName?: string,
	 *   } &
	 *   React.AnchorHTMLAttributes<HTMLAnchorElement>
	 * } props
	 * @returns {React.ReactElement | null} ...
	 */
	function Link({
		addExternalIcon = false,
		internalClassName,
		externalClassName,
		externalIconClassName,
		className,
		href,
		children,
		...rest
	}) {
		// all "internal" links start with a "/" or "#"
		if (href?.startsWith('/') || href?.startsWith('#')) {
			const href2 = (() => {
				const matches = href.match(URL_PAGES_REGEX);
				if (matches !== null) {
					const [pagePath, pageHash] = (() => {
						switch (matches[1]) {
							case 'pages':
								if (matches[2] === 'homepage') {
									return ['', ''];
								} else {
									return [matches[2], ''];
								}

							case 'artists':
								return ['map', `#${matches[2]}`];

							case 'blog-posts':
								return ['blog', `#${matches[2]}`];

							default:
								return [];
						}
					})();

					if (pagePath) {
						const meta = pageMetadataForPath(pagePath);
						if (meta !== null) {
							return `${meta.path}${pageHash}`;
						}
					}
				}

				return href;
			})();

			return (
				<LocaleLink
					{...rest}
					className={internalClassName || className || style.link}
					to={href2}
				>
					{children}
				</LocaleLink>
			);
		}

		return (
			<a
				{...rest}
				className={externalClassName || className || style.link}
				href={href}
				target="_blank"
				// > Note: When using target, add rel="noreferrer noopener" to avoid
				// > exploitation of the window.opener API;
				// > <https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a>
				rel="external noopener noreferrer"
			>
				{addExternalIcon && (
					<ExternalLinkIcon
						className={externalIconClassName || style.externalLinkIcon}
					/>
				)}
				{children}
			</a>
		);
	}
);
